import { LoadingOutlined } from '@ant-design/icons'
import clsx from 'clsx'
import type React from 'react'

import styles from './B2becButton.module.scss'

interface BaseButtonProps {
  children: React.ReactNode
  className?: string
  ghost?: boolean
  block?: boolean
  disabled?: boolean
  loading?: boolean
  icon?: React.ReactNode
}

type ButtonProps = BaseButtonProps & {
  href?: never
  type?: 'submit' | 'reset' | 'button'
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps>

type AnchorProps = BaseButtonProps & {
  href: string
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseButtonProps>

type B2becButtonProps = ButtonProps | AnchorProps

const isAnchorProps = (props: B2becButtonProps): props is AnchorProps => {
  return 'href' in props
}

const B2becButton = ({
  ref,
  children,
  className,
  ghost = false,
  block = false,
  disabled = false,
  loading = false,
  icon = null,
  type = 'button',
  ...props
}: B2becButtonProps & {
  ref?: React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>
}) => {
  const classnames = clsx(
    styles.button,
    ghost && styles.ghost,
    block && styles.block,
    loading && styles.loading,
    disabled && styles.disabled,
    className,
  )

  const renderIcon = () => {
    if (loading) {
      return (
        <span className={clsx(styles.icon, styles.loading)}>
          <LoadingOutlined />
        </span>
      )
    }

    if (icon) {
      return <span className={styles.icon}>{icon}</span>
    }

    return null
  }

  if (isAnchorProps(props)) {
    return (
      <a
        className={classnames}
        {...props}
        ref={ref as React.Ref<HTMLAnchorElement>}
      >
        {renderIcon()}
        <span className={styles.content}>{children}</span>
      </a>
    )
  }

  return (
    <button
      className={classnames}
      type={type}
      {...props}
      ref={ref as React.Ref<HTMLButtonElement>}
    >
      {renderIcon()}
      <span className={styles.content}>{children}</span>
    </button>
  )
}

B2becButton.displayName = 'B2becButton'

// ButtonLink types
type ButtonLinkProps = {
  navigate: () => void
} & Omit<ButtonProps, 'onClick'>

const ButtonLink = ({
  ref,
  navigate,
  ...props
}: ButtonLinkProps & { ref?: React.RefObject<HTMLButtonElement | null> }) => {
  return <B2becButton ref={ref} {...props} onClick={navigate} />
}

ButtonLink.displayName = 'ButtonLink'

export default B2becButton
export const B2becButtonLink = ButtonLink
